import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Clock1, Clock3, Clock6, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const intervals = [
  { value: "15", label: "15 minutes", icon: Clock1 },
  { value: "30", label: "30 minutes", icon: Clock3 },
  { value: "60", label: "60 minutes", icon: Clock6 },
];

/** Convert total minutes to { days, hours, minutes } */
const fromMinutes = (total: number | null) => {
  if (!total || total <= 0) return { days: "", hours: "", minutes: "" };
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  return {
    days: d > 0 ? String(d) : "",
    hours: h > 0 ? String(h) : "",
    minutes: m > 0 ? String(m) : "",
  };
};

/** Convert { days, hours, minutes } to total minutes, or null if all empty */
const toMinutes = (vals: { days: string; hours: string; minutes: string }): number | null => {
  const d = parseInt(vals.days) || 0;
  const h = parseInt(vals.hours) || 0;
  const m = parseInt(vals.minutes) || 0;
  const total = d * 1440 + h * 60 + m;
  return total > 0 ? total : null;
};

const BookingSettings = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const orgId = profile?.organisation_id;

  const [selected, setSelected] = useState("30");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoConfirm, setAutoConfirm] = useState(false);

  const [minLead, setMinLead] = useState({ days: "", hours: "", minutes: "" });
  const [maxAdvance, setMaxAdvance] = useState({ days: "", hours: "", minutes: "" });
  const [minLeadSnap, setMinLeadSnap] = useState(false);
  const [maxAdvanceSnap, setMaxAdvanceSnap] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const fetch = async () => {
      const { data } = await supabase
        .from("organisations")
        .select("booking_min_lead_time, booking_max_advance_time, booking_min_lead_snap_to_day, booking_max_advance_snap_to_day, booking_slot_interval, auto_confirm_bookings")
        .eq("id", orgId)
        .single();
      if (data) {
        setMinLead(fromMinutes((data as any).booking_min_lead_time));
        setMaxAdvance(fromMinutes((data as any).booking_max_advance_time));
        setMinLeadSnap((data as any).booking_min_lead_snap_to_day ?? false);
        setMaxAdvanceSnap((data as any).booking_max_advance_snap_to_day ?? false);
        setSelected(String((data as any).booking_slot_interval ?? 30));
        setAutoConfirm((data as any).auto_confirm_bookings ?? false);
      }
      setLoading(false);
    };
    fetch();
  }, [orgId]);

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);

    const minVal = toMinutes(minLead);
    const maxVal = toMinutes(maxAdvance);

    // Validate: min lead shouldn't exceed max advance
    if (minVal && maxVal && minVal >= maxVal) {
      toast.error("Minimum lead time must be less than maximum advance time.");
      setSaving(false);
      return;
    }

    const { error } = await supabase
      .from("organisations")
      .update({
        booking_min_lead_time: minVal ?? 0,
        booking_max_advance_time: maxVal,
        booking_min_lead_snap_to_day: minLeadSnap,
        booking_max_advance_snap_to_day: maxAdvanceSnap,
        booking_slot_interval: parseInt(selected) || 30,
        auto_confirm_bookings: autoConfirm,
      } as any)
      .eq("id", orgId);

    setSaving(false);
    if (error) {
      toast.error("Failed to save booking settings.");
    } else {
      toast.success("Booking settings saved.");
    }
  };

  const TimeInput = ({
    label,
    description,
    value,
    onChange,
  }: {
    label: string;
    description: string;
    value: { days: string; hours: string; minutes: string };
    onChange: (v: { days: string; hours: string; minutes: string }) => void;
  }) => (
    <div>
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <p className="text-xs text-muted-foreground mb-3">{description}</p>
      <div className="flex gap-3">
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground mb-1 block">{t("booking.days")}</Label>
          <Input
            type="number"
            min="0"
            max="365"
            placeholder="0"
            value={value.days}
            onChange={(e) => onChange({ ...value, days: e.target.value })}
          />
        </div>
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground mb-1 block">{t("booking.hours")}</Label>
          <Input
            type="number"
            min="0"
            max="23"
            placeholder="0"
            value={value.hours}
            onChange={(e) => onChange({ ...value, hours: e.target.value })}
          />
        </div>
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground mb-1 block">{t("booking.minutes_label")}</Label>
          <Input
            type="number"
            min="0"
            max="59"
            placeholder="0"
            value={value.minutes}
            onChange={(e) => onChange({ ...value, minutes: e.target.value })}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <h1 className="text-xl font-bold text-foreground mb-6">{t("settings.booking")}</h1>

      {/* Time Slot Interval */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <p className="text-sm font-medium text-foreground mb-3">{t("booking.interval")}</p>
        <div className="grid grid-cols-3 gap-3">
          {intervals.map((item) => (
            <button
              key={item.value}
              onClick={() => setSelected(item.value)}
              className={cn(
                "flex flex-col items-center gap-2 rounded-xl border p-4 transition-all",
                selected === item.value
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "border-border hover:border-primary/30 hover:bg-muted/50"
              )}
            >
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
                  selected === item.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
              </div>
              <span className={cn("text-sm font-medium", selected === item.value ? "text-foreground" : "text-muted-foreground")}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Auto-confirm Bookings */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">{t("booking.auto_confirm_label")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("booking.auto_confirm_desc")}</p>
          </div>
          <Switch checked={autoConfirm} onCheckedChange={setAutoConfirm} />
        </div>
      </section>

      {/* Booking Window */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <p className="text-sm font-medium text-foreground mb-1">{t("booking.window_title")}</p>
        <p className="text-xs text-muted-foreground mb-5">{t("booking.window_desc")}</p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <TimeInput
                label={t("booking.min_lead_label")}
                description={t("booking.min_lead_desc")}
                value={minLead}
                onChange={setMinLead}
              />
              <div className="flex items-center gap-3 mt-3">
                <Switch checked={minLeadSnap} onCheckedChange={setMinLeadSnap} />
                <Label className="text-sm text-muted-foreground">{t("booking.min_lead_snap")}</Label>
              </div>
            </div>
            <div>
              <TimeInput
                label={t("booking.max_advance_label")}
                description={t("booking.max_advance_desc")}
                value={maxAdvance}
                onChange={setMaxAdvance}
              />
              <div className="flex items-center gap-3 mt-3">
                <Switch checked={maxAdvanceSnap} onCheckedChange={setMaxAdvanceSnap} />
                <Label className="text-sm text-muted-foreground">{t("booking.max_advance_snap")}</Label>
              </div>
            </div>
          </div>
        )}

        <Button className="mt-6" onClick={handleSave} disabled={saving || loading}>
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {t("booking.save")}
        </Button>
      </section>
    </>
  );
};

export default BookingSettings;
